/**
 * TranscriptSpeakersAdapter.ts
 *
 * Adapter implementing ITranscriptVideoCreator for the Transcript & Speakers
 * Content Studio tab. Provides an independent editing context (time, playback,
 * undo/redo) while proxying shared state (story, detections, toast) from the
 * real VideoCreatorStore.
 *
 * Property categories:
 *   Independent – time, isPlaying, canUndo, canRedo, undo, redo
 *   Shared      – story, sceneDetections, faceDetections, toasts,
 *                 getSourceVideoFrameRate, addShareableImage, fixTranscription,
 *                 punchListManager, getDefaultAspectRatio, updateStory
 *   Stubbed     – renderer, currentVideo, sidebarOptions, getFreeMediaTrack,
 *                 recalculateTimelineScale*, refreshSourceAndPlay,
 *                 getShowSubtitles, setShowSubtitles, refetchSubtitles,
 *                 getMaxTrack, getNaturalMediaDimensions, setTime (local only)
 *
 * Created as part of transcript dependency injection refactor (Step 8).
 * See: docs/refactors/transcript-dependency-injection.md
 */

import { isRefusedStoryTranscriptCommand } from '@src/components/transcript/storyTranscriptEditLock';
import { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import { Transcript } from '@src/editor/models/Transcript';
import {
  fixDuplicateElementIds,
  isForcedAlignedTranscript,
} from '@src/editor/services/transcriptValidations';
import { saveVideoResources } from '@src/services/VideoResourcesProvider';
import { wireTranslationPhraseResolver } from '@src/stores-v2/wireTranslationPhraseResolver';
import {
  VideoDimensionsHeight,
  VideoDimensionsWidth,
  VideoResolution,
} from '@src/types.ts/video';
import {
  resolveClipMediaDuration,
  transcriptEndSeconds,
} from '@src/utility/clipMediaSource';
import { ensureElementZPositions } from '@src/utility/general';
import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, runInAction } from 'mobx';
import { v4 as uuid } from 'uuid';

import type { Command } from '@src/editor/commands/Command';
import type { NaturalDimensions } from '@src/editor/types/aspectRatio';
import type { FaceDetection } from '@src/hooks/useFaceDetections';
import type { TranscriptRepository } from '@src/repositories/TranscriptRepository';
import type { SceneData } from '@src/services/framing/SceneDetectionService';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { Toast, ToastRequest } from '@src/stores/toast';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import type { SidebarOption } from '@src/types.ts/general';
import type { ShareableImageType, Story, Video } from '@src/types.ts/story';
import type {
  TranscriptData,
  TranscriptElement,
} from '@src/types.ts/transcript';
import type { AspectRatio } from '@src/types.ts/video';

const logger = LogManager.getLogger('TranscriptSpeakersAdapter');

/** Resolution used for the Transcript & Speakers tab preview */
const TRANSCRIPT_PREVIEW_VIDEO_RES = VideoResolution.Low;

/** Maximum number of retry attempts for transcript persistence */
const MAX_SAVE_RETRY_ATTEMPTS = 3;

interface TranscriptSpeakersAdapterOptions {
  realVideoCreator: IVideoCreatorStore;
  videoSourceStore: VideoSourceStore;
  transcriptRepository: TranscriptRepository;
  transcriptionStore: {
    isTranscriptionReady: boolean;
    transcriptionElements: any[];
    language: string;
    getOriginalTranscription(): {
      elements: any[];
      language: string;
      version?: number;
      forcedAlignment?: TranscriptData['forcedAlignment'];
    } | null;
    getTranscriptionText(elements?: any[]): string;
    getFinalTranscriptionElements(): any[];
    replaceTranscriptVersion(data: TranscriptData): void;
    setOriginalTranscription(data: TranscriptData): void;
  };
}

export class TranscriptSpeakersAdapter implements ITranscriptVideoCreator {
  // ── Independent state ──────────────────────────────────────────────
  time = 0;
  isPlaying = false;
  isBackgroundSaving = false;
  // Set by the review workspace while open (PLAT-10286). Inert here — this
  // adapter has no renderer (see below) — but declared so the review's
  // assignment lands on a real field rather than a dynamic one.
  suppressRendererPlayback = false;

  // Undo/redo stacks (snapshot-based, same pattern as ClipVideoCreatorAdapter)
  private _undoStack: any[][] = [];
  private _redoStack: any[][] = [];

  // ── References ─────────────────────────────────────────────────────
  /** @internal Exposed for clipProcessorDepsFromAdapter — not part of ITranscriptVideoCreator */
  readonly realVideoCreator: IVideoCreatorStore;
  private scopedEditor: ClipPreviewEditorStore;
  private videoSourceStore: VideoSourceStore;
  private transcriptionStore: TranscriptSpeakersAdapterOptions['transcriptionStore'];
  private transcriptRepository: TranscriptRepository;

  // ── Persistence ────────────────────────────────────────────────────
  private saveInProgress = false;
  private saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Story-level transcript editing lock state (see `storyTranscriptEditLock.ts`).
   * Locked until the host reports the `enableStoryTranscriptEditing` flag, so a
   * command issued before flags arrive is refused rather than let through.
   */
  private storyTranscriptEditingLocked = true;
  private persistRetryCount = 0;
  private isInitialized = false;
  /** When a save is already in-flight, queue one follow-up persist instead of dropping it. */
  private _pendingPersist = false;

  constructor(options: TranscriptSpeakersAdapterOptions) {
    this.realVideoCreator = options.realVideoCreator;
    this.videoSourceStore = options.videoSourceStore;
    this.transcriptionStore = options.transcriptionStore;
    this.transcriptRepository = options.transcriptRepository;

    // Create scoped editor backed by its own VideoSourceStore
    this.scopedEditor = new ClipPreviewEditorStore(this.videoSourceStore);
    // Wire the captions translation phrase resolver onto this clip-scoped
    // editor for its lifetime (shared story-level translationStore) so
    // ProduceKaraokeCommand emits translated captions rather than falling
    // back to the original-language path.
    wireTranslationPhraseResolver(
      this.scopedEditor.editor,
      this.realVideoCreator.rootStore.translationStore,
    );

    makeAutoObservable(this, {
      realVideoCreator: false,
      scopedEditor: false,
      videoSourceStore: false,
      transcriptionStore: false,
      transcriptRepository: false,
      suppressRendererPlayback: false,
    } as any);
  }

  // ── Scoped editor access (used by createClipRootProxy) ────────────
  getScopedEditor(): ClipPreviewEditorStore {
    return this.scopedEditor;
  }

  getVideoSourceStore(): VideoSourceStore {
    return this.videoSourceStore;
  }

  // ── Stubbed properties ─────────────────────────────────────────────
  get renderer(): undefined {
    return undefined;
  }

  get currentVideo(): undefined {
    return undefined;
  }

  get sidebarOptions(): SidebarOption {
    // Return a sensible default – the Transcript component reads this but
    // doesn't depend on it for transcript-specific functionality.
    return 'transcript' as SidebarOption;
  }

  // ── Shared properties (proxied from real store) ────────────────────
  get story(): Story | null | undefined {
    return this.realVideoCreator.story;
  }

  get storyId(): string | null | undefined {
    return this.realVideoCreator.storyId;
  }

  get sceneDetections(): SceneData[] | null {
    return this.realVideoCreator.sceneDetections;
  }

  get faceDetections(): FaceDetection[] | null {
    return this.realVideoCreator.faceDetections;
  }

  get toasts(): Toast[] {
    return this.realVideoCreator.toasts;
  }

  showToast(toast: ToastRequest): number {
    return this.realVideoCreator.showToast(toast);
  }

  updateToast(id: number, toast: ToastRequest): number {
    return this.realVideoCreator.updateToast(id, toast);
  }

  dismissToast(id: number): void {
    this.realVideoCreator.dismissToast(id);
  }

  clearToasts(): void {
    this.realVideoCreator.clearToasts();
  }

  get punchListManager(): ITranscriptVideoCreator['punchListManager'] {
    return this.realVideoCreator.punchListManager;
  }

  async setActiveElements(...elementIds: string[]): Promise<void> {
    return this.realVideoCreator.setActiveElements(...elementIds);
  }

  // ── Undo / Redo ────────────────────────────────────────────────────
  get canUndo(): boolean {
    return this._undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this._redoStack.length > 0;
  }

  undo(): void {
    if (!this.canUndo) return;
    const currentSnapshot = this.scopedEditor.editor.getFullTranscript();
    this._redoStack.push([...currentSnapshot]);
    const previous = this._undoStack.pop();
    if (!previous) return;
    this.scopedEditor.editor.loadTranscript(previous, previous);
    this.scopedEditor.refreshTranscriptElements();
    this.schedulePersistence();
  }

  redo(): void {
    if (!this.canRedo) return;
    const currentSnapshot = this.scopedEditor.editor.getFullTranscript();
    this._undoStack.push([...currentSnapshot]);
    const next = this._redoStack.pop();
    if (!next) return;
    this.scopedEditor.editor.loadTranscript(next, next);
    this.scopedEditor.refreshTranscriptElements();
    this.schedulePersistence();
  }

  /**
   * Take an undo snapshot before a mutation. Called externally by command
   * wrappers or the Transcript component before applying edits.
   */
  pushUndoSnapshot(): void {
    const snapshot = this.scopedEditor.editor.getFullTranscript();
    this._undoStack.push([...snapshot]);
    // Clear redo stack on new mutation
    this._redoStack = [];
  }

  // ── Playback control (independent, no renderer) ────────────────────
  async setTime(time: number, _pauseIfPlaying?: boolean): Promise<void> {
    runInAction(() => {
      this.time = time;
    });
  }

  refreshSourceAndPlay(): void {
    // No-op – no renderer in transcript tab
  }

  // ── Subtitles (stubbed) ────────────────────────────────────────────
  getShowSubtitles(): boolean {
    return false;
  }

  setShowSubtitles(_show: boolean): void {
    // No-op
  }

  // ── Timeline operations (stubbed) ──────────────────────────────────
  getMaxTrack(): number {
    return -1; // not supported creating element on the timeline
  }

  getFreeMediaTrack(
    _type: 'audio' | 'video' | 'image',
    _mediaDuration: number,
    _currTime?: number,
  ): number | undefined {
    return undefined;
  }

  recalculateTimelineScale(): void {
    // No-op
  }

  recalculateTimelineScaleIfAtDefaultZoom(): void {
    // No-op
  }

  // ── Media operations ───────────────────────────────────────────────
  async getNaturalMediaDimensions(): Promise<
    Record<string, NaturalDimensions>
  > {
    return {};
  }

  getSourceVideoFrameRate(): number | undefined {
    return this.realVideoCreator.getSourceVideoFrameRate();
  }

  getDefaultAspectRatio(): AspectRatio {
    return this.realVideoCreator.getDefaultAspectRatio();
  }

  // ── Story operations (shared) ──────────────────────────────────────
  addShareableImage(shareableImage: ShareableImageType): void {
    this.realVideoCreator.addShareableImage(shareableImage);
  }

  async updateStory(story: Story): Promise<Story> {
    return this.realVideoCreator.updateStory(story);
  }

  fixTranscription(): void {
    this.realVideoCreator.fixTranscription();
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Editor initialization
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Initialize the scoped editor with the original transcript.
   * Replicates the essential parts of VideoCreatorStore.createNewVideoFromSource()
   * + renderVideo() to load a minimal video project for transcript editing.
   */
  async initializeEditor(): Promise<void> {
    const story = this.realVideoCreator.story;
    if (!story?.originalVideo) {
      logger.warn('Cannot initialize editor – no story or originalVideo');
      return;
    }

    logger.debug('Initializing TranscriptSpeakersAdapter editor');

    try {
      // 1. Get original transcription from transcription store
      const originalTranscription =
        this.transcriptionStore.getOriginalTranscription();
      if (!originalTranscription) {
        logger.warn('No original transcription available');
        return;
      }

      // 2. Build default video source (mirrors VideoCreatorStore.getDefaultSource).
      //    Transcript first: mid-pending the pipeline has not probed the file
      //    (Sidecar Path: transcript lands minutes before `Ingest File`), so
      //    the transcript end is the only media length available (PLAT-11697).
      const videoSource = this.buildDefaultVideoSource(
        story,
        transcriptEndSeconds(originalTranscription.elements),
      );

      // 3. Load project into scoped editor
      this.scopedEditor.loadProjectForPreview({
        videoSource,
        transcript: originalTranscription.elements,
        originalTranscription: originalTranscription.elements,
        forcedAlignment: isForcedAlignedTranscript(originalTranscription),
      });

      // 4. Patch editor.executeCommand for auto-undo + persistence
      this.patchEditorCommands();

      runInAction(() => {
        this.isInitialized = true;
      });

      logger.debug('TranscriptSpeakersAdapter editor initialized');
    } catch (error) {
      logger.error(
        'Failed to initialize TranscriptSpeakersAdapter editor',
        error,
      );
    }
  }

  /**
   * Monkey-patch editor.executeCommand so every command automatically:
   *  1. Takes an undo snapshot before execution (if the command opts in).
   *  2. Schedules persistence after a successful execution.
   *
   * Mirrors the pattern used in ClipVideoCreatorAdapter.
   */
  private patchEditorCommands(): void {
    const editor = this.scopedEditor.editor;
    const originalExecute = editor.executeCommand.bind(editor);

    editor.executeCommand = (command: Command) => {
      // Story-level transcript editing lock: refuse any command that would
      // change the story transcript's element count, whichever surface issued
      // it (Transcript & Speakers tab, Review workspace original pane, a key
      // press). See `storyTranscriptEditLock.ts` for why.
      if (
        isRefusedStoryTranscriptCommand(
          command.getName(),
          this.storyTranscriptEditingLocked,
        )
      ) {
        logger.warn(
          'Refused a story-level transcript edit that would change the element count',
          { command: command.getName() },
        );
        return false;
      }

      let shouldSnapshot = false;
      try {
        shouldSnapshot = !!command?.shouldCreateUndoRedoEntry?.();
      } catch {
        // Guard – treat as no-snapshot if the check itself fails
      }

      if (shouldSnapshot) {
        try {
          this.pushUndoSnapshot();
        } catch (err) {
          logger.error('Failed to take undo snapshot before command', err);
        }
      }

      const result = originalExecute(command);

      if (shouldSnapshot && result) {
        this.schedulePersistence();
      }

      return result;
    };
  }

  /**
   * Build the default video source for the original video.
   * Mirrors VideoCreatorStore.getDefaultSource() logic.
   *
   * `transcriptEnd` (last transcript element end, seconds) stands in for the
   * media length while `originalVideo.video.duration` is still null — the
   * same rule the clip surfaces use (`resolveClipMediaDuration`). A 0 s video
   * element selects no transcript tokens, so the track would load empty.
   */
  private buildDefaultVideoSource(
    story: Story,
    transcriptEnd: number | null,
  ): any {
    const sourceUrl = this.getVideoSourceUrl(
      story,
      TRANSCRIPT_PREVIEW_VIDEO_RES,
    );
    const chapters = story.originalVideo?.chapters;
    const duration = resolveClipMediaDuration(story, {
      transcriptEndSeconds: transcriptEnd,
    });

    let elements: Record<string, unknown>[] = [];

    if (chapters && chapters.length > 1) {
      // Multi-chapter (concatenated) video
      let currentTimelinePosition = 0;
      chapters.forEach((chapter: any, index: number) => {
        const chapterId = uuid();
        const startTime = parseFloat(
          String(chapter.start_time || chapter.start / 1000),
        );
        const endTime = parseFloat(
          String(chapter.end_time || chapter.end / 1000),
        );
        const chapterDuration = endTime - startTime;

        elements.push({
          id: chapterId,
          duration: `${chapterDuration} s`,
          track: 1,
          time: currentTimelinePosition,
          type: 'video',
          source: sourceUrl,
          trim_start: startTime,
          trim_duration: chapterDuration,
          media_duration: duration ?? 0,
          autoplay: true,
          name: chapter.tags?.title || `Clip ${index + 1}`,
        });
        currentTimelinePosition += chapterDuration;
      });
    } else {
      // Single video
      const id = uuid();
      elements = [
        {
          id,
          duration: duration ? `${duration} s` : '0 s',
          track: 1,
          time: 0,
          type: 'video',
          source: sourceUrl,
          autoplay: true,
          trim_start: 0,
          media_duration: duration ?? 0,
        },
      ];
    }

    const width = this.getDefaultWidth(story);
    const height = this.getDefaultHeight(story);

    return ensureElementZPositions({
      output_format: 'mp4',
      width,
      height,
      frame_rate: story.originalVideo?.video?.framerate,
      elements,
    });
  }

  /**
   * Resolve the appropriate video source URL for the given resolution.
   * Mirrors VideoCreatorStore.getValidVideoSourceUrlForResLevel().
   */
  private getVideoSourceUrl(story: Story, resLevel: VideoResolution): string {
    const video = story.originalVideo?.video;
    let sourceVideoUrl: string | undefined;

    if (resLevel === VideoResolution.HD) {
      sourceVideoUrl = video?.mp4UrlHd;
    } else if (resLevel === VideoResolution.High) {
      sourceVideoUrl = video?.mp4UrlHigh;
    } else if (resLevel === VideoResolution.Low) {
      sourceVideoUrl = video?.mp4UrlLow;
    } else if (resLevel === VideoResolution.Medium) {
      sourceVideoUrl = video?.mp4UrlMedium;
    }

    if (!sourceVideoUrl) {
      logger.warn('No video source URL for resolution level, using original');
      sourceVideoUrl = story.originalVideo?.url;
    }

    return sourceVideoUrl || '';
  }

  /**
   * Get default width clamped to standard dimensions.
   * Mirrors VideoCreatorStore.getDefaultWidth().
   */
  private getDefaultWidth(story: Story): number {
    const width = story.originalVideo?.width;
    if (width) {
      return Math.min(
        Math.max(width, VideoDimensionsWidth.AR_9_16),
        VideoDimensionsWidth.AR_16_9,
      );
    }
    return VideoDimensionsWidth.AR_16_9;
  }

  /**
   * Get default height clamped to standard dimensions.
   * Mirrors VideoCreatorStore.getDefaultHeight().
   */
  private getDefaultHeight(story: Story): number {
    const height = story.originalVideo?.height;
    if (height) {
      return Math.min(
        Math.max(height, VideoDimensionsHeight.AR_9_16),
        VideoDimensionsHeight.AR_16_9,
      );
    }
    return VideoDimensionsHeight.AR_16_9;
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Video snapshot for clip creation
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Build a Video object representing the full original video with the
   * adapter's current (possibly edited) transcript state.
   *
   * This is the `sourceVideo` parameter for `saveClipFromTranscriptTab()`.
   * AIClipProcessor.newVideoEditor() reads:
   *  - videoSource — the timeline elements (from scoped editor)
   *  - transcriptionSnapshot — the edited transcript (from scoped editor)
   *  - title, aspectRatio — from the story's original video
   *
   * See: docs/refactors/transcript-dependency-injection.md (Step 11)
   */
  buildVideoSnapshot(): Video {
    const story = this.realVideoCreator.story;
    if (!story?.originalVideo) {
      throw new Error(
        'buildVideoSnapshot: no story or originalVideo available',
      );
    }

    const videoSource = this.scopedEditor.editor.timeline.toStoredJSON();
    const transcriptElements =
      this.scopedEditor.editor.getFullTranscript() as unknown as TranscriptElement[];

    const aspectRatio: AspectRatio =
      this.realVideoCreator.getDefaultAspectRatio();

    return {
      title: story.title || 'Untitled',
      videoSource: videoSource || {},
      transcriptionSnapshot: { elements: transcriptElements },
      aspectRatio,
      videoStatus: 'editing' as Video['videoStatus'],
      sourcePlatform: 'creator-studio',
      associatedVideos: [],
      extraElementData: {},
      videoFilePrimary: story.originalVideo,
    } as Video;
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Persistence – debounced transcript save
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Schedule a debounced save of the current transcript state to the API.
   * Replicates the MobX reaction from useInitializeEditorForOriginalTranscript.
   */
  /**
   * Host-reported lock state from the `enableStoryTranscriptEditing` flag. The
   * refusal in `patchEditorCommands` reads it on every command, so a flag
   * change takes effect without re-creating the adapter.
   */
  setStoryTranscriptEditingLocked(locked: boolean): void {
    this.storyTranscriptEditingLocked = locked;
  }

  schedulePersistence(): void {
    if (!this.isInitialized) return;

    if (this.saveDebounceTimer) {
      clearTimeout(this.saveDebounceTimer);
    }

    this.saveDebounceTimer = setTimeout(() => {
      this.saveDebounceTimer = null;
      this.persistTranscript();
    }, 1500);
  }

  /**
   * Re-densify transcript indices, then persist — run immediately before a
   * translation generation/regeneration (PLAT-9511 whitespace/index fix).
   *
   * In-session inserted/replaced content carries `initial_index: -1` (the
   * plugin's synthetic sentinel; the same value synthetic whitespace uses).
   * Reloading the current transcript runs it back through `new Transcript`,
   * which re-stamps every element's `initial_index` from its array position —
   * so in-session `-1` inserts become real indices. Whitespace is re-stripped
   * to `-1` by `getFullTranscript` and is excluded from the translation index
   * space on the backend. Net: the persisted transcript the backend translates
   * carries NO `-1` *content* index, so the stored translation's
   * `originalIndices` stay clean (no `-1`, no `p_-1`) and align with what the
   * editor renders — which now uses the same re-densified indices.
   *
   * Indices are stable within this generation, not across edits (cross-edit
   * staleness is PLAT-10266's concern). Mirrors the reload pattern used by
   * `undo`/`redo` above, which loads a `getFullTranscript()` snapshot back in.
   */
  async reindexAndPersistForTranslation(): Promise<void> {
    if (!this.isInitialized) return;
    // Repair duplicate element ids here too, not just in `persistTranscript`:
    // the editor reloads from this array, so repairing before the reload keeps
    // the in-memory transcript and the persisted one agreeing on the ids the
    // translation will key against (PLAT-10876). Otherwise the returned
    // translation references repaired ids the editor doesn't have yet.
    const originalTranscript = [
      ...fixDuplicateElementIds(
        new Transcript(
          this.scopedEditor.editor.getFullTranscript(),
        ).getOriginalTranscription(),
      ),
    ];
    this.scopedEditor.editor.loadTranscript(
      originalTranscript,
      originalTranscript,
    );
    this.scopedEditor.refreshTranscriptElements();
    // Persist silently: this save is an internal pre-step of translation, not
    // a user-initiated transcript save. Firing "Transcript saved successfully"
    // here reads as if the *translation* finished the instant Generate is
    // clicked (PLAT-10635).
    await this.flushPersistence({ silent: true });
  }

  /**
   * Flush any pending persistence immediately. If no debounced save is
   * scheduled, triggers a fresh persist. Used by the Cmd+S save service.
   *
   * `silent` suppresses the success toast — used by the pre-translation
   * reindex, where the save is an internal step, not a user-facing save.
   */
  async flushPersistence(options?: { silent?: boolean }): Promise<void> {
    if (!this.isInitialized) return;

    if (this.saveDebounceTimer) {
      // A save was already scheduled – clear the timer and fire now
      clearTimeout(this.saveDebounceTimer);
      this.saveDebounceTimer = null;
    }

    // Always persist (either flushing the pending one or forcing a fresh save)
    await this.persistTranscript(options);
  }

  private async persistTranscript(options?: {
    silent?: boolean;
  }): Promise<void> {
    if (this.saveInProgress) {
      // Queue one follow-up persist rather than silently dropping the request.
      // Mirrors the _pendingNextSave pattern in ClipVideoCreatorAdapter.
      this._pendingPersist = true;
      logger.debug('persistTranscript: save in progress, queuing follow-up');
      return;
    }

    const storyId = this.realVideoCreator.story?.id;
    if (!storyId) {
      logger.warn('No story ID available for transcript save');
      return;
    }

    // Never persist a transcript with duplicate element ids: they make the
    // translation join key ambiguous and the backend rejects the whole
    // translation (PLAT-10876). Repairs transcripts corrupted before the fix.
    const elements = fixDuplicateElementIds(
      this.scopedEditor.transcriptElements,
    );
    if (!elements.length) return;

    this.saveInProgress = true;

    try {
      const transcriptData: TranscriptData = {
        elements: elements as TranscriptData['elements'],
        language: this.transcriptionStore.language,
        fullText: this.transcriptionStore.getTranscriptionText(
          elements as TranscriptData['elements'],
        ),
        version:
          (this.transcriptionStore.getOriginalTranscription()?.version || 0) +
          1,
      };

      // Update store so new clips use edited version
      this.transcriptionStore.setOriginalTranscription(transcriptData);
      logger.debug('Updated originalTranscription in store');

      // Upload to S3
      const resourcesMetadata = await saveVideoResources(storyId, {
        transcriptionSnapshot: transcriptData,
      });

      const fileKey = (resourcesMetadata as any).transcriptionSnapshot?.fileKey;
      if (!fileKey) {
        throw new Error('No fileKey returned from saveVideoResources');
      }

      logger.debug('Uploaded transcript to S3', { fileKey });

      // Update transcript reference via repository
      await this.transcriptRepository.updateTranscriptReference(
        storyId,
        fileKey,
      );

      // Reset retry count on success
      this.persistRetryCount = 0;

      if (!options?.silent) {
        this.showToast({
          state: 'success',
          message: 'Transcript saved successfully',
        });
      }
    } catch (error) {
      logger.error(
        `Failed to persist transcript (attempt ${this.persistRetryCount + 1}/${MAX_SAVE_RETRY_ATTEMPTS})`,
        error,
      );

      if (this.persistRetryCount < MAX_SAVE_RETRY_ATTEMPTS - 1) {
        this.persistRetryCount++;
        this.showToast({
          state: 'warning',
          message: `Transcript save failed, retrying (${this.persistRetryCount}/${MAX_SAVE_RETRY_ATTEMPTS - 1})...`,
        });
        // Retry via schedulePersistence to avoid tight retry loops
        this.schedulePersistence();
      } else {
        this.persistRetryCount = 0;
        this.showToast({
          state: 'error',
          message:
            'Failed to save transcript after multiple attempts. Please try again.',
        });
      }
    } finally {
      this.saveInProgress = false;
      // Drain the pending queue: fire one more persist if requested while we
      // were saving (mirrors _pendingNextSave drain in ClipVideoCreatorAdapter).
      if (this._pendingPersist) {
        this._pendingPersist = false;
        void this.persistTranscript();
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Cleanup
  // ═══════════════════════════════════════════════════════════════════

  dispose(): void {
    if (this.saveDebounceTimer) {
      clearTimeout(this.saveDebounceTimer);
    }
    this.scopedEditor.dispose();
    logger.debug('TranscriptSpeakersAdapter disposed');
  }
}
